import {useAtom} from 'jotai';
import {isEqual} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {LoadingBar} from '@/components/LoadingBar';
import {AnalysisItem} from '@/map/@types';
import {useNearbyHospitalByPoint} from '@/map/api';
import {selectedNearbyInfoItemCardAtom} from '@/map/atom';
import {AROUND_DATA_OPTIONS} from '@/map/constants';
import {AreaAnalyticsFilter} from '@/map/enums';
import {ColumnFlexBox} from '@/styled-components';
import {palette} from '@/styles';
import {withCommaWithUnit} from '@/utils';
import {SpotRoundMark} from '../../../Marker';
import {HospitalMarkers} from '../../../MarkerCluster/NearbyInfo';
import {ErrorBox} from '../ErrorBox';
import {NoData} from '../NoData';
import {ReportContainer} from '../StyledComponents';
import {NearbyInfoTable} from './Table';

interface IProps {
  map?: naver.maps.Map;
  item: AnalysisItem;
}

const type = AreaAnalyticsFilter.HOSPITAL;

export const HospitalReport = ({map, item}: IProps) => {
  const [activeItem, setActiveItem] = useAtom(selectedNearbyInfoItemCardAtom);

  const {data, isPending, error} = useNearbyHospitalByPoint(item);

  const tableData = useMemo(() => {
    if (data) {
      return data.map((i) => [
        i.name,
        withCommaWithUnit(i.bedsCount, {placeholder: '정보 없음'}),
      ]);
    }
  }, [data]);

  const onMarkerClick = useCallback(
    (index: number) => {
      if (!data) {
        return;
      }

      const item = data[index];

      setActiveItem((prev) => {
        if (prev && isEqual(item, prev.item)) {
          return null;
        }

        return {
          type,
          item,
        };
      });
    },
    [data, setActiveItem]
  );

  useEffect(() => {
    return () => {
      setActiveItem(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <ReportContainer>
        <ErrorBox />
      </ReportContainer>
    );
  } else if (isPending) {
    return (
      <ReportContainer>
        <LoadingBar color={palette.textSecondary} />
      </ReportContainer>
    );
  }

  if (!data?.length || !tableData?.length) {
    return (
      <ReportContainer>
        <NoData text="분석 지역 내 종합 병원 정보가 없습니다." />
      </ReportContainer>
    );
  }

  return (
    <>
      <HospitalMarkers
        map={map}
        items={data}
        selectedItem={activeItem?.item}
        onClick={onMarkerClick}
      />
      <ReportContainer>
        <ColumnFlexBox gap={40}>
          <NearbyInfoTable
            title="병원"
            headers={[
              {width: '60%', label: '종합병원'},
              {width: '40%', label: '병상 수'},
            ]}
            sumLabel="병원이"
            icon={<Icon />}
            data={tableData}
          />
        </ColumnFlexBox>
      </ReportContainer>
    </>
  );
};

const Icon = () => {
  const options = AROUND_DATA_OPTIONS[type];

  return <SpotRoundMark typeLabel={options.label} {...options.mobile} />;
};
